import { useFeatureGuideStore } from '@/stores/featureGuide'
import { GUIDE_CONTENT } from './guide-content'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog'
import { Lightbulb, Check } from 'lucide-react'

export function FeatureGuideModal() {
  const { isOpen, activeFeature, closeGuide, dismissGuide } = useFeatureGuideStore()

  const guide = activeFeature ? GUIDE_CONTENT[activeFeature] : null

  if (!guide) return null

  const accentColor = `hsl(var(${guide.accentVar}))`

  const handleClose = () => {
    if (activeFeature) {
      dismissGuide(activeFeature)
    }
    closeGuide()
  }

  return (
    <Dialog open={isOpen} onOpenChange={(open) => { if (!open) handleClose() }}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <div className="flex items-center gap-2.5">
            <div
              className="w-2.5 h-2.5 rounded-full shrink-0"
              style={{ backgroundColor: accentColor }}
            />
            <DialogTitle className="text-xl">{guide.title}</DialogTitle>
          </div>
          <DialogDescription className="text-sm mt-1.5 leading-relaxed">
            {guide.description}
          </DialogDescription>
        </DialogHeader>

        {/* Feature Highlights */}
        <div className="mt-4 space-y-2.5">
          {guide.highlights.map((highlight, idx) => (
            <div key={idx} className="flex gap-2.5">
              <div
                className="shrink-0 w-5 h-5 rounded-full flex items-center justify-center mt-0.5"
                style={{ backgroundColor: `hsl(var(${guide.accentVar}) / 0.12)` }}
              >
                <Check className="w-3 h-3" style={{ color: accentColor }} />
              </div>
              <p className="text-sm text-gray-700 leading-relaxed">{highlight}</p>
            </div>
          ))}
        </div>

        {/* Workflow Steps - compact */}
        <div className="mt-5">
          <h3 className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-3">
            Workflow
          </h3>
          <div className="flex items-start gap-0">
            {guide.steps.map((step, idx) => (
              <div key={idx} className="flex-1 flex flex-col items-center text-center relative">
                {/* Connector line */}
                {idx < guide.steps.length - 1 && (
                  <div
                    className="absolute top-3 left-[calc(50%+14px)] right-[calc(-50%+14px)] h-px"
                    style={{ backgroundColor: `hsl(var(${guide.accentVar}) / 0.25)` }}
                  />
                )}
                <div
                  className="w-6 h-6 rounded-full flex items-center justify-center text-xs font-semibold text-white relative z-10"
                  style={{ backgroundColor: accentColor }}
                >
                  {idx + 1}
                </div>
                <p className="text-xs font-medium text-gray-800 mt-1.5 leading-tight">{step.title}</p>
                <p className="text-[11px] text-gray-400 mt-0.5 leading-tight px-1">{step.description}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Tips */}
        {guide.tips && guide.tips.length > 0 && (
          <div className="mt-5 p-4 rounded-lg" style={{ backgroundColor: `hsl(var(${guide.accentVar}) / 0.06)` }}>
            <div className="flex items-center gap-2 mb-2">
              <Lightbulb className="w-4 h-4" style={{ color: accentColor }} />
              <h3 className="text-sm font-semibold" style={{ color: accentColor }}>Tips</h3>
            </div>
            <ul className="space-y-1.5">
              {guide.tips.map((tip, idx) => (
                <li key={idx} className="text-sm text-gray-600 flex items-start gap-2">
                  <span className="text-gray-400 mt-0.5 shrink-0">&bull;</span>
                  {tip}
                </li>
              ))}
            </ul>
          </div>
        )}

        <DialogFooter className="mt-4">
          <Button onClick={handleClose} style={{ backgroundColor: accentColor }}>
            Got it!
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
